/**
 * URL state for the task planner's cursor pagination.
 *
 * The URL carries at most two params: `cursor`, the opaque token that opened
 * the page currently on screen, and `dir`, which way that token was walked.
 * Neither present means page 1.
 *
 * One token rather than two because a page is opened by exactly one of them.
 * The API spells the two directions as separate query params -- `cursor` walks
 * forward (toward older rows), `before` walks backward -- and sending both is a
 * 400, since they name different pages and there is no sane winner. `dir` is
 * what decides which name the one token goes out under; see `pageQuery`.
 *
 * What the URL deliberately does NOT carry is a page number. These listings are
 * keyset paginated: a page is a cursor boundary, not an ordinal, so there is no
 * page N to jump to and nothing to address. The server reports `page_number`
 * for display only -- derived per-request from the limit that request used --
 * and the pager renders it without ever sending it back.
 *
 * Not every listing answers all of this. `has_prev`/`prev_cursor` and the
 * counting fields are omitted, not zeroed, on endpoints that do not support
 * them, and today only `GET /{orgId}/task-activations`,
 * `GET /{orgId}/selector-sessions` and `GET /{orgId}/task-plans` populate any.
 * Routes on the other endpoints -- the load plan history and the per-plan
 * activations list -- stay forward-only until their endpoint catches up, which
 * is why absence has to keep meaning "not reported" rather than "no".
 */

/** Which way the token in the URL was walked. Absent means forward. */
export type PageDirection = "next" | "prev";

/** The pagination half of a route's validated search params. */
export type PageSearch = { cursor?: string; dir?: PageDirection };

/**
 * A page to navigate to: the token, and the direction it came from. What the
 * pager hands up and what the route writes into the URL.
 */
export type PageToken = { cursor: string; dir: PageDirection };

/**
 * Search-param validation. Returns `undefined` for anything that is not a
 * non-empty string, so page 1 carries no `cursor` param at all.
 */
export function validateCursor(raw: unknown): string | undefined {
  return typeof raw === "string" && raw.length > 0 ? raw : undefined;
}

function validateDirection(raw: unknown): PageDirection | undefined {
  return raw === "next" || raw === "prev" ? raw : undefined;
}

/**
 * Validate both pagination params together.
 *
 * A `dir` with no `cursor` names nothing, so it is dropped rather than kept as
 * a direction pointing at no token -- that would send `before=undefined` and
 * make page 1 look like a backward walk. A `cursor` with no `dir` is a forward
 * walk, which is both the default and what links written before `dir` existed
 * look like.
 */
export function validatePageSearch(
  search?: Record<string, unknown>,
): PageSearch {
  const cursor = validateCursor(search?.["cursor"]);

  if (typeof cursor === "undefined") {
    return {};
  }

  const dir = validateDirection(search?.["dir"]);
  return typeof dir === "undefined" ? { cursor } : { cursor, dir };
}

/**
 * Spread into a list request's `query`. Maps the single URL token onto whichever
 * of the API's two mutually exclusive params matches its direction, and
 * contributes nothing at all on page 1.
 */
export function pageQuery(page: PageSearch): {
  cursor?: string;
  before?: string;
} {
  if (typeof page.cursor === "undefined") {
    return {};
  }

  return page.dir === "prev"
    ? { before: page.cursor }
    : { cursor: page.cursor };
}
